// WhatsApp short-link redirect page — https://link.ourteam.ai/s/<short_code>
// (ticket #2935). Candidate-side, NO login: resolve the code via the BE once,
// then forward to the original URL. The resolved URL carries the candidate's
// auth token, so it is never rendered/logged/persisted — the only use is an
// immediate window.location.replace (replace keeps the back button clean).
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2Icon, Link2OffIcon, ClockIcon, RotateCcwIcon } from "lucide-react";
import ourteamLogo from "@asset/ourteam_logo.png";
import { Button } from "@component/button";
import { resolveShortLink } from "@service/short-link";
import type { ShortLinkResolveResult } from "@service/short-link";

type ViewState =
    | { kind: "redirecting" }
    | Exclude<ShortLinkResolveResult, { kind: "ok" }>;

export default function ShortLinkRedirectPage({
    // #3269: the /wa/:code ad-link route reuses this page with the /wa resolver.
    resolve = resolveShortLink,
}: {
    resolve?: (shortCode: string) => Promise<ShortLinkResolveResult>;
}) {
    const { code } = useParams<{ code: string }>();
    const [state, setState] = useState<ViewState>({ kind: "redirecting" });
    // A successful resolve counts a click on the BE — guard so the automatic
    // attempt fires exactly ONCE per visit (no double-invoke on remounts, no
    // auto-retry loop). Manual retry bypasses this deliberately.
    const attemptedRef = useRef(false);

    const attempt = useCallback(async () => {
        if (!code) {
            setState({ kind: "not_found" });
            return;
        }
        setState({ kind: "redirecting" });
        const result = await resolve(code);
        if (result.kind === "ok") {
            // Keep showing "Redirecting…" while the browser navigates.
            window.location.replace(result.originalUrl);
            return;
        }
        setState(result);
    }, [code, resolve]);

    useEffect(() => {
        if (attemptedRef.current) return;
        attemptedRef.current = true;
        void attempt();
    }, [attempt]);

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
            <div className="w-full max-w-sm text-center">
                {/* Brand wordmark — BUNDLED asset (ships with the JS bundle), not
                    the CDN: candidates open these links on flaky mobile
                    connections, and a CDN fetch can stall and leave the logo
                    half-painted or missing. Explicit dimensions avoid layout
                    shift (832×178 intrinsic → 128×~27). */}
                <img
                    src={ourteamLogo}
                    alt="ourteam"
                    width={128}
                    className="mx-auto mb-8"
                />
                {state.kind === "redirecting" && (
                    <>
                        <Loader2Icon className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
                        <h1 className="text-lg font-semibold text-gray-900 mb-1">
                            Redirecting…
                        </h1>
                        <p className="text-sm text-gray-500">
                            Taking you to your link.
                        </p>
                    </>
                )}

                {state.kind === "not_found" && (
                    <>
                        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                            <Link2OffIcon className="w-7 h-7 text-gray-400" />
                        </div>
                        <h1 className="text-lg font-semibold text-gray-900 mb-1">
                            Link not found
                        </h1>
                        <p className="text-sm text-gray-500">
                            This link doesn't exist or may have been removed.
                            Please check the link from your message and try again.
                        </p>
                    </>
                )}

                {state.kind === "expired" && (
                    <>
                        <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
                            <ClockIcon className="w-7 h-7 text-amber-500" />
                        </div>
                        <h1 className="text-lg font-semibold text-gray-900 mb-1">
                            Link expired
                        </h1>
                        <p className="text-sm text-gray-500">{state.message}</p>
                        <p className="text-xs text-gray-400 mt-2">
                            Please request a new link from the company that
                            contacted you.
                        </p>
                    </>
                )}

                {state.kind === "error" && (
                    <>
                        <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
                            <Link2OffIcon className="w-7 h-7 text-rose-400" />
                        </div>
                        <h1 className="text-lg font-semibold text-gray-900 mb-1">
                            Couldn't open the link
                        </h1>
                        <p className="text-sm text-gray-500 mb-4">
                            Something went wrong reaching the server. Check your
                            connection and try again.
                        </p>
                        <Button onClick={() => void attempt()}>
                            <RotateCcwIcon className="w-4 h-4" />
                            Try again
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}
